import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { professions } from "../asset/js/Professions";
import returnCityNames from "../asset/js/CityNames";
import { trackEvent } from "../asset/js/analytics";

const REGION_MAP = [
  ["צפון", "southCities"],
  ["קרית שמונה והסביבה", "northCities"],
  ["חיפה והסביבה", "westCities"],
  ["ירושלים והסביבה", "eastCities"],
  ["נתניה והסביבה", "eCities"],
  ["השרון והסביבה", "fCities"],
  ["מרכז", "gCities"],
  ["שפלה והסביבה", "hCities"],
  ["באר שבע והסביבה", "iCities"],
  ["דרום", "jCities"],
];

const DURATION_LABELS = {
  "0.166": "10 דקות",
  "0.5": "30 דקות",
  "1": "שעה",
  "3": "3 שעות",
  "6": "6 שעות",
  "12": "12 שעות",
  "24": "יום",
  "48": "יומיים",
  "72": "3 ימים",
  "96": "4 ימים",
  "120": "5 ימים",
  "144": "6 ימים",
  "168": "7 ימים",
};

const MAX_QUESTION_ROUNDS = 7;

const TENDER_EXAMPLES = [
  "מעוניין בהובלה של מקרר מרמת גן לתל אביב...",
  "מעוניינת לבנות קיר גבס בין המטבח לסלון...",
  "צריך לצבוע דירת 4 חדרים לפני כניסה...",
  "מחפשת הנדימן להרכבת ארון ותליית מדפים...",
  "צריך להחליף ברז ולתקן נזילה מתחת לכיור...",
  "מעוניינת בהתקנת מזגן בחדר השינה...",
  "צריך לפרק מקלחון ישן ולהתקין חדש...",
  "מחפש חשמלאי להוספת נקודות חשמל בסלון...",
  "מעוניינת בניקיון יסודי לדירה אחרי שיפוץ...",
  "צריך לתקן תריס חשמלי שלא עולה...",
];


function getIsraelHour() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jerusalem",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  return hour === 24 ? 0 : hour;
}

function getWelcomeMessage() {
  const hour = getIsraelHour();
  const firstName = (Cookies.get("name") || "").trim();
  const nameSuffix = firstName ? `, ${firstName}` : "";

  let greeting = `היי${nameSuffix}`;
  let messages = [];

  if (hour >= 5 && hour < 12) {
    greeting = `בוקר טוב${nameSuffix}`;
    messages = [
      "זה זמן מצוין להעלות את העבודה. בעלי המקצוע באזור יוכלו להתחיל לקבל אותה כבר היום.",
      "ספרו לנו מה צריך לעשות, ואנחנו נעזור לכם להפוך את זה למכרז ברור ומדויק.",
      "העבודה שלכם במרכז. מתחילים בכמה מילים ומתקדמים משם יחד.",
    ];
  } else if (hour >= 12 && hour < 17) {
    greeting = `צהריים טובים${nameSuffix}`;
    messages = [
      "יש עבודה שצריך לקדם? ספרו לנו עליה עכשיו ונכין לכם מכרז מסודר.",
      "כמה מילים על העבודה מספיקות כדי להתחיל. אנחנו כבר נעזור לדייק את השאר.",
      "זה בדיוק הזמן להוציא את המשימה מהראש ולהעביר אותה לבעלי המקצוע המתאימים.",
    ];
  } else if (hour >= 17 && hour < 23) {
    greeting = `ערב טוב${nameSuffix}`;
    messages = [
      "בעלי המקצוע אולי כבר בבית, אבל המכרז שלכם יכול לצאת לדרך עכשיו והם יקבלו עליו הודעה.",
      "לא צריך לדחות למחר. ספרו לנו מה צריך לעשות והמכרז שלכם כבר יתחיל לעבוד בשבילכם.",
      "אתם מפרסמים עכשיו, בעלי המקצוע הרלוונטיים יקבלו את המכרז ויוכלו להציע כשנוח להם.",
    ];
  } else {
    greeting = `לילה טוב${nameSuffix}`;
    messages = [
      "אפשר לפרסם גם עכשיו. המכרז יחכה לבעלי המקצוע ויגיע אליהם בזמן הנכון.",
      "סוגרים את היום עם משימה אחת פחות בראש. ספרו לנו מה צריך לעשות ונכין את המכרז.",
      "גם בשעה הזו אפשר להתחיל. אתם מתארים את העבודה, ואנחנו דואגים שהמכרז יהיה מוכן להתקדם.",
    ];
  }

  return {
    greeting,
    message: messages[Math.floor(Math.random() * messages.length)],
  };
}

function getCurrentDateContext() {
  const now = new Date();
  const dateFormatter = new Intl.DateTimeFormat("he-IL", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "long",
  });
  const timeFormatter = new Intl.DateTimeFormat("he-IL", {
    timeZone: "Asia/Jerusalem",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return {
    iso: now.toLocaleString("sv-SE", { timeZone: "Asia/Jerusalem" }).replace(" ", "T"),
    dateLabel: dateFormatter.format(now),
    timeLabel: timeFormatter.format(now),
  };
}

function calculateRecommendedDurationHours(targetExecutionDate) {
  if (!targetExecutionDate) return null;

  const target = new Date(targetExecutionDate);
  if (Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const hoursUntilWork = (target.getTime() - now.getTime()) / (60 * 60 * 1000);

  // עבודה מיידית, היום או מחר: מכרז של יום אחד.
  if (hoursUntilWork <= 48) return 24;

  // משאירים לפחות יומיים בין סיום המכרז למועד העבודה, ועד 7 ימי מכרז.
  const fullDaysUntilWork = Math.floor(hoursUntilWork / 24);
  const tenderDays = Math.max(1, Math.min(7, fullDaysUntilWork - 2));
  return tenderDays * 24;
}

function extractJson(value) {
  if (!value) return null;
  const text = String(value).trim();
  try {
    return JSON.parse(text);
  } catch (_) {
    const match = text.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]);
    } catch (_) {
      return null;
    }
  }
}

function compactTranscript(items, tailSize = 6) {
  if (!Array.isArray(items) || items.length === 0) return "";

  const firstUser = items.find((item) => item.role === "user");
  const tail = items.slice(-tailSize);
  const compact = [];

  if (firstUser && !tail.includes(firstUser)) compact.push(firstUser);
  compact.push(...tail);

  return compact
    .map(
      (item) =>
        `${item.role === "user" ? "לקוח" : "סייען"}:${String(
          item.text || ""
        ).trim()}`
    )
    .join("\n");
}

function inferAreaFromTranscript(transcript, cityNames) {
  const customerText = transcript
    .filter((item) => item.role === "user")
    .map((item) => String(item.text || "").toLowerCase())
    .join(" ");

  for (const [region, key] of REGION_MAP) {
    const cities = cityNames[key] || [];
    const match = cities.find((city) => {
      const normalized = String(city || "").trim().toLowerCase();
      return normalized.length >= 2 && customerText.includes(normalized);
    });
    if (match) return region;
  }
  return null;
}

function normalizeRecommendedProfessions(transcript, recommendedProfession) {
  const requested = Array.isArray(recommendedProfession)
    ? recommendedProfession.filter((p) => professions.includes(p))
    : professions.includes(recommendedProfession)
    ? [recommendedProfession]
    : [];

  const customerText = transcript
    .filter((item) => item.role === "user")
    .map((item) => item.text)
    .join(" ")
    .toLowerCase();

  const hasAny = (terms) => terms.some((term) => customerText.includes(term));

  const paintTerms = [
    "צבע",
    "צביעה",
    "לצבוע",
    "סיד",
    "סיוד",
    "שפכטל",
  ];
  const nonPaintWorkTerms = [
    "שיפוץ",
    "לשפץ",
    "מקלחון",
    "ארון",
    "ארונית",
    "מדף",
    "מדפים",
    "הרכבה",
    "להרכיב",
    "התקנה",
    "להתקין",
    "ריצוף",
    "קרמיקה",
    "ברז",
    "כיור",
    "אסלה",
    "דלת",
    "חלון",
    "גבס",
    "איטום",
    "פירוק",
  ];
  const renovationTerms = [
    "שיפוץ",
    "לשפץ",
    "מקלחון",
    "קרמיקה",
    "ריצוף",
    "גבס",
    "פירוק",
  ];
  const majorRenovationTerms = [
    "בניית חדר",
    "לבנות חדר",
    "תוספת חדר",
    "תוספת בניה",
    "תוספת בנייה",
    "שיפוץ קומפלט",
    "שיפוץ מלא",
    "שיפוץ כללי",
    "כל הדירה",
    "דירה שלמה",
    "הריסה מלאה",
    "שבירת קירות",
    "קיר נושא",
  ];

  const hasPaint = hasAny(paintTerms);
  const hasNonPaintWork = hasAny(nonPaintWorkTerms);
  const hasRenovationWork =
    requested.includes("שיפוצים") || hasAny(renovationTerms);
  const isMajorRenovation = hasAny(majorRenovationTerms);

  let normalized = [...new Set(requested)];

  // "צבעי" מתאים לעבודת צבע מובהקת. בעבודה משולבת הוא לא המקצוע המוביל.
  if (normalized.includes("צבעי") && hasPaint && hasNonPaintWork) {
    normalized = normalized.filter((p) => p !== "צבעי");
  }

  // בעבודות שיפוץ קטנות-בינוניות הנדימן הוא התאמה חשובה בנוסף לשיפוצים.
  if (hasRenovationWork && !isMajorRenovation) {
    normalized = normalized.filter((p) => p !== "הנדימן");
    normalized.unshift("הנדימן");
    if (!normalized.includes("שיפוצים")) normalized.push("שיפוצים");
  }

  // אם זו עבודה מעורבת של צבע + תיקון/הרכבה, העדף הנדימן על פני צבעי.
  if (hasPaint && hasNonPaintWork && !isMajorRenovation) {
    normalized = normalized.filter((p) => p !== "צבעי" && p !== "הנדימן");
    normalized.unshift("הנדימן");
  }

  return normalized.filter((p) => professions.includes(p)).slice(0, 2);
}

function BidAssistantHero() {
  const navigate = useNavigate();
  const cityNames = useMemo(() => returnCityNames(), []);
  const welcomeMessage = useMemo(() => getWelcomeMessage(), []);
  const [started, setStarted] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [questionRounds, setQuestionRounds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [exampleText, setExampleText] = useState("");
  const [exampleDeleting, setExampleDeleting] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const assistantSessionId = useRef(
    `tender-assistant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const conversationRef = useRef(null);
  const analyticsStartedRef = useRef(false);

  useEffect(() => {
    if (started || result || input || inputFocused) return undefined;

    const fullExample = TENDER_EXAMPLES[exampleIndex].replace(/\.{3}$/, "");
    let delay = exampleDeleting ? 32 : 58;

    if (!exampleDeleting && exampleText === fullExample) {
      delay = 1350;
    } else if (exampleDeleting && exampleText === "") {
      delay = 260;
    }

    const timeout = window.setTimeout(() => {
      if (!exampleDeleting) {
        if (exampleText === fullExample) {
          setExampleDeleting(true);
          return;
        }
        setExampleText(fullExample.slice(0, exampleText.length + 1));
        return;
      }

      if (exampleText === "") {
        setExampleDeleting(false);
        setExampleIndex((current) => (current + 1) % TENDER_EXAMPLES.length);
        return;
      }

      setExampleText(fullExample.slice(0, Math.max(0, exampleText.length - 1)));
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [started, result, input, inputFocused, exampleIndex, exampleText, exampleDeleting]);

  useEffect(() => {
    if (!conversationRef.current || messages.length === 0 || result) return;

    const frame = window.requestAnimationFrame(() => {
      const container = conversationRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [messages, result]);

  const chatEndpoint = `${process.env.React_APP_BACKEND_URL || "https://laravelapi.bidit.co.il"}/api/chat`;
  const progressValue = result
    ? 100
    : Math.min(88, Math.max(10, ((questionRounds + (started ? 1 : 0)) / (MAX_QUESTION_ROUNDS + 1)) * 100));

  const callChat = async (message, logOptions = {}) => {
    const response = await fetch(chatEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        source: "tender_assistant",
        session_id: assistantSessionId.current,
        display_message: logOptions.displayMessage ?? message,
        hide_user_log: Boolean(logOptions.hideUserLog),
      }),
    });
    if (!response.ok) throw new Error("chat request failed");
    const data = await response.json();
    return data?.reply || "";
  };

  const finalizeTender = async (transcript) => {
    const currentDate = getCurrentDateContext();

    const allowedProfessions = professions
      .map((name, index) => `${index}:${name}`)
      .join("|");

    const prompt =
      `סכם מכרז Bidit. החזר JSON תקין בלבד.\n` +
      `זמן ישראל: ${currentDate.dateLabel}, ${currentDate.timeLabel}; ${currentDate.iso}. פרש תאריכים יחסיים לפי זמן זה.\n` +
      `אל תמציא פרטים. title עד 70 תווים; description ברור ומספיק לבעל מקצוע לתמחור.\n` +
      `professionIds: 1-2 מזהים בלבד מרשימת המקצועות. אסור להחזיר מקצוע שאינו ברשימה. צבעי לצבע מובהק; הנדימן לעבודות קטנות/בינוניות משולבות; שיפוצים לעבודות גדולות/מלאות.\n` +
      `recommendedArea: אחד מהאזורים ${REGION_MAP.map(([region]) => region).join(", ")} או "לא ידוע".\n` +
      `targetExecutionDate: ISO עם offset ישראלי; יום ללא שעה=12:00; אין מועד/אין העדפה=null.\n` +
      `recommendedDurationHours: אחד מ-24,48,72,96,120,144,168; סיים לפחות יומיים לפני ביצוע; בלי מועד/דחיפות=72. durationReason קצר.\n` +
      `JSON: {"title":"","description":"","professionIds":[],"recommendedArea":"","targetExecutionDate":null,"recommendedDurationHours":72,"durationReason":""}\n` +
      `מקצועות: ${allowedProfessions}\n` +
      `שיחה:\n${compactTranscript(transcript, 10)}`;

    const reply = await callChat(prompt, { hideUserLog: true });
    const parsed = extractJson(reply);
    if (!parsed?.title || !parsed?.description) {
      throw new Error("invalid final response");
    }

    const calculatedDuration = calculateRecommendedDurationHours(parsed.targetExecutionDate);
    const recommendedDuration = calculatedDuration ?? parsed.recommendedDurationHours;
    const durationKey = String(recommendedDuration ?? "");
    const normalized = {
      title: parsed.title,
      description: parsed.description,
      recommendedProfession: normalizeRecommendedProfessions(
        transcript,
        Array.isArray(parsed.professionIds)
          ? parsed.professionIds
              .map((id) => professions[Number(id)])
              .filter(Boolean)
          : []
      ),
      recommendedArea:
        inferAreaFromTranscript(transcript, cityNames) ||
        (REGION_MAP.some(([region]) => region === parsed.recommendedArea)
          ? parsed.recommendedArea
          : "לא ידוע"),
      recommendedDurationHours: DURATION_LABELS[durationKey]
        ? Number(durationKey)
        : 72,
      durationReason: calculatedDuration
        ? `מועד העבודה מאפשר להשאיר את המכרז פתוח ${DURATION_LABELS[String(calculatedDuration)]} ועדיין להשאיר זמן לפני הביצוע.`
        : parsed.durationReason ||
          "משך שמאפשר לקבל מספר הצעות בלי לעכב את ביצוע העבודה.",
    };
    setResult(normalized);
    trackEvent("assistant_completed", {
      question_rounds: questionRounds,
      recommended_duration_hours: normalized.recommendedDurationHours,
      recommended_area_known: normalized.recommendedArea !== "לא ידוע",
    });
  };

  const getNextQuestion = async (transcript, round) => {
    if (round >= MAX_QUESTION_ROUNDS) {
      await finalizeTender(transcript);
      return;
    }

    const prompt =
      `אתה מאפיין מכרז Bidit. JSON בלבד.\n` +
      `מבחן עצירה: האם בעל מקצוע יכול להבין ולתמחר בלי לנחש פרט מהותי? אם לא, שאל.\n` +
      `שאל שאלה אחת קצרה וישירה, עד 16 מילים, בלי הקדמה ובלי לחזור על מידע שכבר נמסר.\n` +
      `ברר רק מה שמשפיע מהותית על סוג העבודה, היקף, כמות/מידות, מצב קיים, פירוק/פינוי, חומרים/גמר, תשתית, נגישות או מחיר.\n` +
      `אם התיאור יכול להתפרש לכמה עבודות שונות, חובה לברר מה מבוצע. לדוגמה "70 מ"ר גבס" מחייב לברר אם מדובר במחיצות, הנמכת תקרה, חיפוי או עבודה אחרת.\n` +
      `התאם את השאלה למקצוע ולעבודה בפועל. אל תשאל פרט שאינו משפיע על הביצוע או התמחור.\n` +
      `לפני ready חייב להיות גם מועד ביצוע, או שהלקוח אמר שאין העדפה.\n` +
      `אם יש מספיק מידע החזר {"status":"ready"}; אחרת {"status":"question","question":"..."}.\n` +
      `שיחה:\n${compactTranscript(transcript, 6)}`;

    const latestUserMessage = [...transcript]
      .reverse()
      .find((item) => item.role === "user")?.text;
    const reply = await callChat(prompt, {
      displayMessage: latestUserMessage || "",
    });
    const parsed = extractJson(reply);

    if (parsed?.status === "ready") {
      await finalizeTender(transcript);
      return;
    }

    const question = parsed?.question || reply;
    if (!question) throw new Error("empty question");
    setMessages([...transcript, { role: "assistant", text: question }]);
    setQuestionRounds(round + 1);
  };

  const submitAnswer = async () => {
    const value = input.trim();
    if (!value || loading || result) return;

    setError("");
    setLoading(true);
    if (!analyticsStartedRef.current) {
      analyticsStartedRef.current = true;
      trackEvent("assistant_started");
    }
    setStarted(true);
    setInput("");

    const transcript = [...messages, { role: "user", text: value }];
    setMessages(transcript);

    try {
      await getNextQuestion(transcript, questionRounds);
    } catch (err) {
      console.error("Bidit assistant error:", err);
      setInput(value);
      setError("לא הצלחתי להמשיך כרגע. אפשר לנסות שוב בעוד רגע.");
    } finally {
      setLoading(false);
    }
  };

  const saveAssistantDraft = () => {
    if (!result) return;
    sessionStorage.setItem(
      "biditAssistantDraft",
      JSON.stringify({ ...result, createdAt: Date.now() })
    );
    sessionStorage.setItem("biditAssistantReturnTo", "/postbid");
  };

  const continueToTender = () => {
    if (!result) return;
    saveAssistantDraft();
    trackEvent("publish_clicked", { authenticated: Boolean(Cookies.get("token")), path: "assistant" });
    navigate(Cookies.get("token") ? "/postbid" : "/signupwithphone");
  };

  const continueToSignIn = () => {
    if (!result) return;
    saveAssistantDraft();
    trackEvent("publish_clicked", { authenticated: false, path: "assistant_signin" });
    navigate("/signin");
  };

  return (
    <Paper
      id="bidit-tender-assistant"
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        direction: "rtl",
        mb: { xs: 2, md: 2.5 },
        px: { xs: 1.4, sm: 2.2, md: 3 },
        py: started || result ? { xs: 2.2, sm: 2.6, md: 3 } : { xs: 1.8, sm: 2.1, md: 2.4 },
        borderRadius: { xs: "16px", md: "20px" },
        border: "1px solid rgba(25,118,210,0.13)",
        background:
          "linear-gradient(135deg, #FFFFFF 0%, #F8FBFE 62%, #FFFDFC 100%)",
        boxShadow: "0 8px 26px rgba(25, 74, 112, 0.07)",
        "&:before": {
          content: '""',
          position: "absolute",
          width: 110,
          height: 110,
          borderRadius: "50%",
          bgcolor: "rgba(25,118,210,0.045)",
          left: -45,
          bottom: -62,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 900, mx: "auto" }}>
        {!started && !result && (
          <Box>
            <Box
              sx={{
                textAlign: "center",
                maxWidth: 760,
                mx: "auto",
                mb: { xs: 1.5, md: 1.8 },
              }}
            >
              <Box
                sx={{
                  mb: 1.05,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.7,
                  px: 1.15,
                  py: 0.55,
                  borderRadius: "999px",
                  bgcolor: "rgba(25,118,210,0.07)",
                  border: "1px solid rgba(25,118,210,0.10)",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "#F28C18",
                    boxShadow: "0 0 0 4px rgba(242,140,24,0.10)",
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Assistant",
                    fontWeight: 700,
                    color: "#315B77",
                    fontSize: { xs: 12.8, md: 13.5 },
                  }}
                >
                  {welcomeMessage.greeting}
                </Typography>
              </Box>

              <Typography
                component="h2"
                sx={{
                  fontFamily: "Assistant",
                  fontWeight: 800,
                  fontSize: { xs: "1.55rem", sm: "1.82rem", md: "2.05rem" },
                  color: "#F28C18",
                  lineHeight: 1.15,
                  letterSpacing: "-0.015em",
                }}
              >
                צריכים בעל מקצוע?
                <Box component="span" sx={{ display: "block", color: "#1976D2", font: "inherit" }}>
                  פרסמו עבודה וקבלו הצעות מחיר בשיטת מכרז דיגיטלי!
                </Box>
              </Typography>

            </Box>
          </Box>
        )}

        {started && !result && (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Assistant",
                    fontWeight: 800,
                    fontSize: { xs: 19, md: 22 },
                    color: "#153F5D",
                  }}
                >
                  מכינים את המכרז שלך
                </Typography>
                <Typography
                  sx={{
                    mt: 0.25,
                    fontFamily: "Assistant",
                    color: "#667D8C",
                    fontSize: 13.5,
                  }}
                >
                  עוד כמה פרטים מקצועיים ואכין עבורך תיאור מסודר לבעלי המקצוע.
                </Typography>
              </Box>

            </Box>

            <Box sx={{ mt: 1.1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.65,
                  color: "#7890A0",
                }}
              >
                <Typography sx={{ fontFamily: "Assistant", fontSize: 12.5 }}>
                  אפיון העבודה
                </Typography>
                <Typography sx={{ fontFamily: "Assistant", fontSize: 12.5 }}>
                  {loading ? "חושב על השאלה הבאה..." : "מתקדמים"}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  height: 5,
                  borderRadius: 20,
                  bgcolor: "rgba(25,118,210,0.09)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 20,
                    background:
                      "linear-gradient(90deg, #1976D2 0%, #318FE5 68%, #F59A23 100%)",
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {messages.length > 0 && !result && (
          <Box
            ref={conversationRef}
            sx={{
              mb: 1.3,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: { xs: 190, md: 210 },
              overflowY: "auto",
              px: 0.3,
            }}
          >
            {messages.slice(-6).map((message, index) => (
              <Box
                key={`${message.role}-${index}`}
                sx={{
                  alignSelf:
                    message.role === "user" ? "flex-start" : "flex-end",
                  maxWidth: { xs: "94%", sm: "82%" },
                  px: 1.4,
                  py: 0.9,
                  borderRadius:
                    message.role === "user"
                      ? "16px 16px 5px 16px"
                      : "16px 16px 16px 5px",
                  bgcolor:
                    message.role === "user" ? "#E8F3FC" : "#FFF3DF",
                  border:
                    message.role === "user"
                      ? "1px solid #D4E8F7"
                      : "1px solid #F8DFC0",
                  color: "#2B4352",
                  fontFamily: "Assistant",
                  fontSize: { xs: 14, md: 14.5 },
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                }}
              >
                {message.text}
              </Box>
            ))}
          </Box>
        )}

        {!result && (
          <Box
            sx={{
              p: { xs: 0.7, sm: 0.8 },
              display: "flex",
              gap: 1,
              alignItems: "stretch",
              flexDirection: { xs: "column", sm: "row" },
              bgcolor: "#FFFFFF",
              border: "1.5px solid rgba(25,118,210,0.34)",
              borderRadius: { xs: "14px", sm: "16px" },
              boxShadow: "0 5px 16px rgba(25,118,210,0.10)",
            }}
          >
            <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
              {!started && !input && !inputFocused && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    zIndex: 2,
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 0.8,
                    px: { xs: 1.65, md: 1.8 },
                    pointerEvents: "none",
                    direction: "rtl",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontSize: { xs: 17, md: 18 },
                      lineHeight: 1,
                      filter: "drop-shadow(0 2px 5px rgba(245,154,35,0.22))",
                    }}
                  >
                    ✨
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontFamily: "Assistant",
                      fontSize: { xs: 14.5, md: 16 },
                      fontWeight: 500,
                      color: "#8A99A3",
                    }}
                  >
                    {exampleText}{!exampleDeleting && exampleText === TENDER_EXAMPLES[exampleIndex].replace(/\.{3}$/, "") ? "..." : ""}
                  </Typography>
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                minRows={started ? 2 : 1}
                maxRows={4}
                value={input}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitAnswer();
                  }
                }}
                placeholder={started ? "כתבו את התשובה כאן..." : inputFocused ? "כתבו כאן את העבודה..." : ""}
                disabled={loading}
                variant="outlined"
                sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Assistant",
                  fontSize: { xs: 15, md: 16.5 },
                  bgcolor: "#FFFFFF",
                  borderRadius: "14px",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(25,118,210,0.15)" },
                  "&.Mui-focused fieldset": { borderColor: "#1976D2" },
                },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#8A99A3",
                    opacity: 1,
                  },
                }}
              />
            </Box>
            <Button
              variant="contained"
              onClick={submitAnswer}
              disabled={loading || !input.trim()}
              endIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendRoundedIcon />
                )
              }
              sx={{
                minWidth: { sm: 125 },
                minHeight: { xs: 46, sm: 50 },
                px: 2.5,
                borderRadius: "14px",
                fontFamily: "Assistant",
                fontWeight: 800,
                fontSize: 15.5,
                textTransform: "none",
                bgcolor: "#F28C18",
                color: "#FFFFFF",
                boxShadow: "0 6px 16px rgba(242,140,24,0.25)",
                "&:hover": { bgcolor: "#DD7B0B" },
                "&.Mui-disabled": {
                  bgcolor: "#F6B86F",
                  color: "#FFFFFF",
                  opacity: 1,
                },
                "& .MuiButton-endIcon": { marginRight: "10px", marginLeft: 0 },
              }}
            >
              {started ? "המשך" : "התחילו לפרסם"}
            </Button>
          </Box>
        )}

        {!started && !result && (
          <Box
            sx={{
              mt: 0.9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.8, sm: 1.4 },
              flexWrap: "wrap",
              color: "#6E8391",
            }}
          >
            {["חינם ללקוחות", "ללא התחייבות", "מחליטים רק בסיום המכרז"].map(
              (text, index) => (
                <React.Fragment key={text}>
                  {index > 0 && (
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: "#F59A23",
                      }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{ fontFamily: "Assistant", fontSize: 12.5 }}
                  >
                    {text}
                  </Typography>
                </React.Fragment>
              )
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, fontFamily: "Assistant" }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box>
            <Box sx={{ textAlign: "center", mb: 2.5 }}>
              <CheckCircleRoundedIcon
                sx={{ fontSize: 52, color: "#22A06B", mb: 0.7 }}
              />
              <Typography
                sx={{
                  fontFamily: "Assistant",
                  fontWeight: 800,
                  fontSize: { xs: 25, md: 31 },
                  color: "#153F5D",
                }}
              >
                המכרז שלך כמעט מוכן
              </Typography>
              <Typography
                sx={{
                  mt: 0.6,
                  fontFamily: "Assistant",
                  color: "#687F8F",
                  fontSize: { xs: 14.5, md: 16 },
                }}
              >
                הכנתי סיכום מסודר. במסך הבא תוכלו לבדוק, לערוך ולבחור בעצמכם את פרטי הפרסום.
              </Typography>
            </Box>

            <Box
              sx={{
                p: { xs: 2, sm: 2.6, md: 3 },
                border: "1px solid #D7E7F2",
                borderRadius: "20px",
                bgcolor: "rgba(255,255,255,0.96)",
                boxShadow: "0 12px 32px rgba(31,80,114,0.08)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Assistant",
                  fontWeight: 800,
                  fontSize: { xs: 18, md: 20 },
                  color: "#173E5A",
                }}
              >
                {result.title}
              </Typography>
              <Typography
                sx={{
                  mt: 1.1,
                  fontFamily: "Assistant",
                  whiteSpace: "pre-wrap",
                  color: "#435B6B",
                  lineHeight: 1.65,
                  fontSize: { xs: 14, md: 14.5 },
                }}
              >
                {result.description}
              </Typography>

              <Box sx={{ mt: 2.2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {result.recommendedProfession.length > 0 && (
                  <Chip
                    label={`מקצוע מומלץ: ${result.recommendedProfession.join(", ")}`}
                    sx={{ fontFamily: "Assistant", bgcolor: "#EAF4FD" }}
                  />
                )}
                {result.recommendedArea !== "לא ידוע" && (
                  <Chip
                    label={`אזור מומלץ: ${result.recommendedArea}`}
                    sx={{ fontFamily: "Assistant", bgcolor: "#EAF4FD" }}
                  />
                )}
                <Chip
                  label={`משך מומלץ: ${
                    DURATION_LABELS[String(result.recommendedDurationHours)] ||
                    "יום"
                  }`}
                  sx={{ fontFamily: "Assistant", bgcolor: "#FFF1DE" }}
                />
              </Box>

              <Typography
                sx={{
                  mt: 1.1,
                  fontFamily: "Assistant",
                  fontSize: 13.5,
                  color: "#718493",
                }}
              >
                {result.durationReason} ההמלצות אינן נבחרות אוטומטית.
              </Typography>

              <Box
                sx={{
                  mt: 2.8,
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {!Cookies.get("token") && (
                  <Box sx={{ width: "100%", mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: "Assistant",
                        fontWeight: 700,
                        fontSize: { xs: 14.5, md: 15 },
                        color: "#435B6B",
                      }}
                    >
                      המכרז שלך מוכן. הרשמה קצרה ונפרסם אותו לבעלי מקצוע מתאימים.
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={continueToTender}
                  endIcon={<SendRoundedIcon />}
                  sx={{
                    minHeight: 48,
                    px: 2.8,
                    fontFamily: "Assistant",
                    fontWeight: 800,
                    fontSize: 15.5,
                    borderRadius: "12px",
                    bgcolor: "#1976D2",
                    boxShadow: "0 8px 20px rgba(25,118,210,0.20)",
                    "&:hover": { bgcolor: "#1268BB" },
                    "& .MuiButton-endIcon": { marginRight: "10px", marginLeft: 0 },
                  }}
                >
                  {Cookies.get("token")
                    ? "פתיחת המכרז"
                    : "הרשמה ופתיחת המכרז בחינם"}
                </Button>
                {!Cookies.get("token") && (
                  <Button
                    variant="text"
                    onClick={continueToSignIn}
                    sx={{
                      fontFamily: "Assistant",
                      fontWeight: 700,
                      borderRadius: "10px",
                      color: "#55758B",
                    }}
                  >
                    כבר יש לי חשבון - להתחברות
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default BidAssistantHero;
